import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import './Alerts.css';
import '../Summary/Summary.css';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '../../Images/search-icon.svg';
import { usePagination } from '../../hooks/usePagination';
import { getAllAlerts, AlertResponse, acknowledgeAllAlerts, getDistrictCustomerSiteInfo, DistrictCustomerSiteInfo, SiteInfo } from '../../Services/serviceURL';
import { CustomerLite } from '../Plants/CreatePlant';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import calendarIcon from '../../Images/calendar.svg';
import ArrowUp from '../../Images/chevron-up.svg';
import ArrowDown from '../../Images/chevron-down.svg';
import ArrowDefault from '../../Images/Chevron-default-icon.svg';

const mapDistrictSiteToCustomerSite = (site: SiteInfo) => ({
    siteId: site.siteId.toString(),
    site: site.name,
    latitude: '0',
    longitude: '0',
});

const mapDistrictCustomerToCustomerLite = (
    item: DistrictCustomerSiteInfo,
    index: number
): CustomerLite => ({
    id: `customer-${index}`,
    customerId: item.salesDistrictCustomer,
    customerName: item.salesDistrictCustomer,
    soldTo: '', // Not provided in the new API response
    sites: item.sites?.map(mapDistrictSiteToCustomerSite) ?? [],
});

const transformDistrictCustomerData = (data: DistrictCustomerSiteInfo[]): CustomerLite[] =>
    data.map((item, index) => mapDistrictCustomerToCustomerLite(item, index));

 const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;

  return date.toLocaleString('en-US', {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

type SortableKeys =
  | 'assetType'
  | 'assetName'
  | 'alarmType'
  | 'sensors'
  | 'actualMeasurement'
  | 'thLL'
  | 'thL'
  | 'thH'
  | 'thHH'
  | 'setOn'
  | 'clearedOn'
  | 'status'
  | 'site'
  | 'plant'
  | 'customer'
  | 'Actions';

type SortConfig = { key: SortableKeys; direction: 'asc' | 'desc' } | null;

interface SortIconProps {
    columnKey: SortableKeys;
    className?: string;
    sortConfig: SortConfig;
    onSort: (key: SortableKeys) => void;
}

const SortIcon: React.FC<SortIconProps> = ({
    columnKey,
    className = '',
    sortConfig,
    onSort,
}) => {
    const isActive = sortConfig?.key === columnKey;
    const direction = isActive ? sortConfig?.direction : null;

    const classNames = ['table-sort-icon'];
    if (direction) {
        classNames.push(`sort-${direction}`);
    }
    if (className) {
        classNames.push(className);
    }

    let sortIconImage;
    if (!isActive) {
        sortIconImage = <img src={ArrowDefault} alt="" className="sort-icon-img" />;
    } else if (direction === 'asc') {
        sortIconImage = <img src={ArrowUp} alt="" className="sort-icon-img-up" />;
    } else {
        sortIconImage = <img src={ArrowDown} alt="" className="sort-icon-img-down" />;
    }

    return (
        <button
            type="button"
            className={classNames.join(' ')}
            aria-label={`Sort by ${columnKey}`}
            onClick={(e) => {
                e.stopPropagation();
                onSort(columnKey);
            }}
        >
            {sortIconImage}
        </button>
    );
};

const CHEVRON_DOWN_PATH = 'M4.16797 7.5L10.0013 13.3333L15.8346 7.5';

const DropdownChevron: React.FC<{ isOpen: boolean }> = ({ isOpen }) => (
    <span className={`alerts-dropdown-arrow ${isOpen ? 'open' : ''}`}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d={CHEVRON_DOWN_PATH}
                stroke="#FCFCFD"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    </span>
);

interface DropdownOptionButtonProps {
    isSelected: boolean;
    onSelect: () => void;
    children: React.ReactNode;
}

const DropdownOptionButton: React.FC<DropdownOptionButtonProps> = ({
    isSelected,
    onSelect,
    children,
}) => (
    <button
        type="button"
        className={`dropdown-option ${isSelected ? 'selected' : ''}`}
        onClick={onSelect}
    >
        {children}
    </button>
);

// Transform API response to match component needs
const transformAlertData = (apiData: AlertResponse[]) => {
    return apiData.map(alert => {
        const transformed = {
            id: alert.id,
            assetId: alert.assetId,
            assetType: alert.assetType,
            assetName: alert.assetName,
            alarmType: alert.alertType,
            sensors: alert.attribute,
            actualMeasurement: alert.actualMeasurement,
            thLL: alert.thresholdLL,
            thL: alert.thresholdL,
            thH: alert.thresholdH,
            thHH: alert.thresholdHH,
            setOn: formatDateTime(alert.startDate),
            setOnTs: alert.startDate ? new Date(alert.startDate).getTime() : undefined,
            clearedOn: alert.endDate ? formatDateTime(alert.endDate) : '-',
            clearedOnTs: alert.endDate ? new Date(alert.endDate).getTime() : undefined,
            status: alert.status,
            site: alert.siteName,
            plant: alert.plantName,
            customer:alert.customerName,
            customerId: alert.customerId,
            customerName: alert.customerName, // Make sure this is included
            siteId: alert.siteId,
            plantId: alert.plantId,
            acknowledged: alert.isAcknowledged,
            canAcknowledge: alert.canAcknowledge,
            parameters: alert.parameters
        };

        return transformed;
    });
};
   //sorting for action
    const getActionSortValue = (row: any) => {
    if (row.acknowledged) return 'acknowledged';
    if (row.canAcknowledge) return 'acknowledge';
    return 'cannot acknowledge';
    };

const renderAcknowledgeAction = (
    row: { acknowledged?: boolean; canAcknowledge?: boolean; id: string },
    onAcknowledge: (alertId: string) => void
) => {
    if (row.acknowledged) {
        return <span className="acknowledged-label">Acknowledged</span>;
    }
    if (row.canAcknowledge) {
        return (
            <button
                type="button"
                className="acknowledge-btn"
                onClick={() => onAcknowledge(row.id)}
            >
                Acknowledge
            </button>
        );
    }
    return <span className="no-access">-</span>;
};

type ExtractedSite = {
    siteId: string;
    site: string;
    customerId: string;
    customerName: string;
};

type ExtractedPlant = {
    plantId: string;
    plantName: string;
    siteId: string;
    siteName: string;
    customerId: string;
    customerName: string;
};

type AlertFilterContext = {
    term: string;
    selectedAlarmType: string;
    selectedStatus: string;
    selectedCustomer: string;
    selectedSite: string;
    selectedPlant: string;
    startMs?: number;
    endMs?: number;
    customers: CustomerLite[];
    extractedSites: ExtractedSite[];
    extractedPlants: ExtractedPlant[];
};

const stringifyValue = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') {
        try {
            return JSON.stringify(value);
        } catch {
            return Object.prototype.toString.call(value);
        }
    }
    return String(value);
};

const normalizeSearchText = (text: unknown): string =>
    stringifyValue(text ?? '').toLowerCase().replaceAll(/\s+/g, ' ').trim();

const formatText = (value: unknown): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') {
        const record = value as Record<string, unknown>;
        if (record.description) return stringifyValue(record.description);
        if (record.name) return stringifyValue(record.name);
        if (record.code) return stringifyValue(record.code);
    }
    return stringifyValue(value);
};

const highlightMatch = (value: unknown) => {
    if (value === null || value === undefined) return '-';
    return stringifyValue(value);
};

const getCustomerLabel = (customer: CustomerLite): string => {
    const soldTo = customer.soldTo;
    return soldTo ? `${customer.customerName} (${soldTo})` : customer.customerName;
};

const filterCustomersBySearch = (
    customers: CustomerLite[],
    customerSearch: string
): CustomerLite[] => {
    const term = normalizeSearchText(customerSearch);
    if (!term) return customers;
    return customers.filter((customer) => {
        const name = normalizeSearchText(customer.customerName);
        const soldTo = normalizeSearchText(customer.soldTo);
        return name === term || soldTo === term || name.includes(term) || soldTo.includes(term);
    });
};

const filterSitesBySearch = (
    extractedSites: ExtractedSite[],
    selectedCustomer: string,
    selectedPlant: string,
    customers: CustomerLite[],
    siteSearch: string
): ExtractedSite[] => {
    let sitesToShow = extractedSites;

    if (selectedCustomer && !selectedPlant) {
        const customer = customers.find((c) => c.customerId === selectedCustomer);
        if (customer) {
            sitesToShow = extractedSites.filter(
                (site) =>
                    site.customerId === selectedCustomer ||
                    site.customerName === customer.customerName
            );
        }
    }

    const term = normalizeSearchText(siteSearch);
    if (!term) return sitesToShow;

    return sitesToShow.filter((site) => {
        const siteName = normalizeSearchText(site.site);
        const siteId = normalizeSearchText(site.siteId);
        const customerName = normalizeSearchText(site.customerName);
        return (
            siteName === term ||
            siteId === term ||
            customerName === term ||
            siteName.includes(term) ||
            siteId.includes(term) ||
            customerName.includes(term)
        );
    });
};

const filterPlantsBySearch = (
    extractedPlants: ExtractedPlant[],
    selectedSite: string,
    selectedCustomer: string,
    plantSearch: string
): ExtractedPlant[] => {
    let plantsToShow = extractedPlants;

    if (selectedSite) {
        plantsToShow = extractedPlants.filter((plant) => plant.siteId === selectedSite);
    } else if (selectedCustomer) {
        plantsToShow = extractedPlants.filter((plant) => plant.customerId === selectedCustomer);
    }

    const term = normalizeSearchText(plantSearch);
    if (!term) return plantsToShow;

    return plantsToShow.filter((plant) => {
        const plantName = normalizeSearchText(plant.plantName);
        const plantId = normalizeSearchText(plant.plantId);
        const siteName = normalizeSearchText(plant.siteName);
        return (
            plantName === term ||
            plantId === term ||
            plantName.includes(term) ||
            plantId.includes(term) ||
            siteName.includes(term)
        );
    });
};

const getAlertDateRangeMs = (
    selectedTimeRange: string,
    startDate: Date | null,
    endDate: Date | null
): { startMs?: number; endMs?: number } => {
    const now = Date.now();
    if (selectedTimeRange === 'Last 24 hours') {
        return { startMs: now - 24 * 60 * 60 * 1000, endMs: now };
    }
    if (selectedTimeRange === '7 days') {
        return { startMs: now - 7 * 24 * 60 * 60 * 1000, endMs: now };
    }
    if (selectedTimeRange === 'Custom date range' && startDate && endDate) {
        const rangeStart = new Date(startDate);
        rangeStart.setHours(0, 0, 0, 0);
        const rangeEnd = new Date(endDate);
        rangeEnd.setHours(23, 59, 59, 999);
        return { startMs: rangeStart.getTime(), endMs: rangeEnd.getTime() };
    }
    return {};
};

const rowMatchesTextSearch = (row: Record<string, unknown>, term: string): boolean => {
    if (!term) return true;
    const fields = [
        row.assetType,
        row.assetName,
        row.alarmType,
        row.sensors,
        row.status,
        row.site,
        row.plant,
        row.setOn,
        row.clearedOn,
        row.customer,
    ];
    return fields.some((value) => stringifyValue(value ?? '').toLowerCase().includes(term));
};

const rowMatchesStatusFilter = (row: Record<string, unknown>, selectedStatus: string): boolean => {
    if (!selectedStatus) return true;
    const rowCode = row.status !== undefined && row.status !== null ? stringifyValue(row.status) : '';
    const rowLabel = stringifyValue(row.status ?? '').toLowerCase();
    return (
        rowCode === selectedStatus ||
        (selectedStatus === 'Active' && rowLabel === 'active') ||
        (selectedStatus === 'Cleared' && rowLabel === 'cleared')
    );
};

const rowMatchesCustomerFilter = (
    row: Record<string, unknown>,
    selectedCustomer: string,
    customers: CustomerLite[]
): boolean => {
    if (!selectedCustomer) return true;
    if (row.customerId === selectedCustomer) return true;
    const selectedCustomerName = customers.find((c) => c.customerId === selectedCustomer)?.customerName;
    return !selectedCustomerName || row.customer === selectedCustomerName;
};

const rowMatchesSiteFilter = (
    row: Record<string, unknown>,
    selectedSite: string,
    extractedSites: ExtractedSite[]
): boolean => {
    if (!selectedSite) return true;
    if (row.siteId === selectedSite || row.site === selectedSite) return true;
    const siteName = extractedSites.find((site) => site.siteId === selectedSite)?.site;
    return siteName ? row.site === siteName : false;
};

const rowMatchesPlantFilter = (
    row: Record<string, unknown>,
    selectedPlant: string,
    extractedPlants: ExtractedPlant[]
): boolean => {
    if (!selectedPlant) return true;
    if (row.plantId === selectedPlant) return true;
    const plantName = extractedPlants.find((plant) => plant.plantId === selectedPlant)?.plantName;
    return plantName ? row.plant === plantName : false;
};

const rowMatchesDateRange = (
    row: Record<string, unknown>,
    startMs?: number,
    endMs?: number
): boolean => {
    if (startMs === undefined || endMs === undefined) return true;
    const timestamp = row.setOnTs as number | undefined;
    return timestamp !== undefined && timestamp >= startMs && timestamp <= endMs;
};

const alertRowMatchesFilters = (row: Record<string, unknown>, ctx: AlertFilterContext): boolean =>
    rowMatchesTextSearch(row, ctx.term) &&
    (!ctx.selectedAlarmType ||
        (row.alarmType as string | undefined)?.toLowerCase() === ctx.selectedAlarmType.toLowerCase()) &&
    rowMatchesStatusFilter(row, ctx.selectedStatus) &&
    rowMatchesCustomerFilter(row, ctx.selectedCustomer, ctx.customers) &&
    rowMatchesSiteFilter(row, ctx.selectedSite, ctx.extractedSites) &&
    rowMatchesPlantFilter(row, ctx.selectedPlant, ctx.extractedPlants) &&
    rowMatchesDateRange(row, ctx.startMs, ctx.endMs);

const getAlertSortValue = (row: Record<string, unknown>, key: SortableKeys): unknown => {
    switch (key) {
        case 'actualMeasurement':
        case 'thLL':
        case 'thL':
        case 'thH':
        case 'thHH':
            return row[key] == null ? null : Number.parseFloat(stringifyValue(row[key]));
        case 'setOn':
            return row.setOnTs ?? 0;
        case 'clearedOn':
            return row.clearedOnTs ?? 0;
        case 'Actions':
            return getActionSortValue(row);
        default:
            return row[key] ?? '';
    }
};

const isEmptySortValue = (value: unknown): boolean =>
    value === undefined || value === null || value === '';

const applySortDirection = (comparison: number, direction: 'asc' | 'desc'): number =>
    direction === 'asc' ? comparison : -comparison;

const compareEmptySortValues = (
    aValue: unknown,
    bValue: unknown,
    direction: 'asc' | 'desc'
): number | null => {
    const aIsEmpty = isEmptySortValue(aValue);
    const bIsEmpty = isEmptySortValue(bValue);
    if (aIsEmpty && bIsEmpty) return 0;
    if (aIsEmpty) return applySortDirection(1, direction);
    if (bIsEmpty) return applySortDirection(-1, direction);
    return null;
};

const compareNumericSortValues = (
    aValue: number,
    bValue: number,
    direction: 'asc' | 'desc'
): number => applySortDirection(aValue - bValue, direction);

const compareStringSortValues = (
    aValue: unknown,
    bValue: unknown,
    direction: 'asc' | 'desc'
): number => {
    const valA = stringifyValue(aValue ?? '').toLowerCase().trim();
    const valB = stringifyValue(bValue ?? '').toLowerCase().trim();
    if (valA === valB) return 0;
    return applySortDirection(valA < valB ? -1 : 1, direction);
};

const compareAlertSortValues = (
    aValue: unknown,
    bValue: unknown,
    direction: 'asc' | 'desc'
): number => {
    const emptyComparison = compareEmptySortValues(aValue, bValue, direction);
    if (emptyComparison !== null) return emptyComparison;

    if (typeof aValue === 'number' && typeof bValue === 'number') {
        return compareNumericSortValues(aValue, bValue, direction);
    }

    return compareStringSortValues(aValue, bValue, direction);
};

const findMatchingCustomerId = (customerName: string, customers: CustomerLite[]): string => {
    const lowerName = customerName.toLowerCase();
    const exactMatch = customers.find((customer) => customer.customerName.toLowerCase() === lowerName);
    if (exactMatch) return exactMatch.customerId;

    const partialMatch = customers.find(
        (customer) =>
            lowerName.includes(customer.customerName.toLowerCase()) ||
            customer.customerName.toLowerCase().includes(lowerName)
    );
    return partialMatch?.customerId ?? '';
};

const resolveCustomerIdForSite = (
    siteId: string,
    extractedSites: ExtractedSite[],
    customers: CustomerLite[]
): string => {
    const siteObj = extractedSites.find((site) => site.siteId === siteId);
    if (!siteObj) return '';
    if (siteObj.customerName) {
        return findMatchingCustomerId(siteObj.customerName, customers);
    }
    return siteObj.customerId ?? '';
};

const resolveCustomerIdForPlant = (
    plantObj: ExtractedPlant | undefined,
    customers: CustomerLite[]
): string => {
    if (!plantObj) return '';

    if (plantObj.customerName) {
        const customerId = findMatchingCustomerId(plantObj.customerName, customers);
        if (customerId) return customerId;
    }

    if (plantObj.customerId) {
        return customers.find((customer) => customer.customerId === plantObj.customerId)?.customerId ?? '';
    }

    return '';
};

const resolveSiteDisplayValue = (
    isSiteOpen: boolean,
    siteSearch: string,
    selectedSite: string,
    selectedPlant: string,
    extractedSites: ExtractedSite[],
    extractedPlants: ExtractedPlant[]
): string => {
    if (isSiteOpen) return siteSearch;
    if (!selectedSite) return '';

    return (
        extractedSites.find((site) => site.siteId === selectedSite)?.site ??
        (selectedPlant ? extractedPlants.find((plant) => plant.plantId === selectedPlant)?.siteName : undefined) ??
        ''
    );
};

const getSelectedPlantName = (
    selectedPlant: string,
    extractedPlants: ExtractedPlant[],
    plants: { plantId: string; plantName: string }[]
): string => {
    if (!selectedPlant) return '';
    return (
        extractedPlants.find((plant) => plant.plantId === selectedPlant)?.plantName ??
        plants.find((plant) => plant.plantId === selectedPlant)?.plantName ??
        ''
    );
};

const getCalendarDayBackground = (
    date: Date,
    startDate: Date | null,
    endDate: Date | null
): string => {
    const isStart = startDate?.toDateString() === date.toDateString();
    const isEnd = endDate?.toDateString() === date.toDateString();
    if (isStart || isEnd) return '#4a5463';
    if (startDate && endDate && date >= startDate && date <= endDate) return 'rgba(74, 84, 99, 0.3)';
    if (date.toDateString() === new Date().toDateString()) return 'rgba(255, 255, 255, 0.1)';
    return 'transparent';
};

const extractSitesAndPlantsFromAlerts = (alertsData: AlertResponse[]) => {
    const siteMap = new Map<string, ExtractedSite>();
    const plantMap = new Map<string, ExtractedPlant>();

    alertsData.forEach((alert) => {
        if (alert.siteId && alert.siteName && !siteMap.has(alert.siteId)) {
            siteMap.set(alert.siteId, {
                siteId: alert.siteId,
                site: alert.siteName,
                customerId: alert.customerId || '',
                customerName: alert.customerName || '',
            });
        }

        if (alert.plantId && alert.plantName && !plantMap.has(alert.plantId)) {
            plantMap.set(alert.plantId, {
                plantId: alert.plantId,
                plantName: alert.plantName,
                siteId: alert.siteId || '',
                siteName: alert.siteName || '',
                customerId: alert.customerId || '',
                customerName: alert.customerName || '',
            });
        }
    });

    const uniqueSites = Array.from(siteMap.values()).sort((a, b) => {
        const customerCompare = a.customerName.localeCompare(b.customerName);
        return customerCompare === 0 ? a.site.localeCompare(b.site) : customerCompare;
    });

    const uniquePlants = Array.from(plantMap.values()).sort((a, b) => {
        const siteCompare = a.siteName.localeCompare(b.siteName);
        return siteCompare === 0 ? a.plantName.localeCompare(b.plantName) : siteCompare;
    });

    return { uniqueSites, uniquePlants };
};

const openDropdownOnArrowKey = (e: React.KeyboardEvent, open: () => void) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        open();
    }
};

const formatPaginationText = (
    totalItems: number,
    currentPage: number,
    itemsPerPage: number
): string => {
    if (totalItems === 0) {
        return 'Showing 0 to 0 of 0 entries';
    }
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalItems);
    return `Showing ${start} to ${end} of ${totalItems} entries`;
};

const formatCalendarButtonLabel = (
    selectedTimeRange: string,
    startDate: Date | null,
    endDate: Date | null
): string => {
    if (selectedTimeRange === 'Custom date range' && startDate && endDate) {
        return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
    }
    return selectedTimeRange;
};

const resolveCustomerInputDisplay = (
    isCustomerOpen: boolean,
    customerSearch: string,
    selectedCustomer: string,
    customers: CustomerLite[]
) => {
    const selectedCustomerName = selectedCustomer
        ? customers.find((c) => c.customerId === selectedCustomer)?.customerName ?? ''
        : '';
    return {
        inputValue: isCustomerOpen ? customerSearch : selectedCustomerName,
        placeholder: isCustomerOpen
            ? 'Search customers...'
            : (selectedCustomerName || 'Customer'),
    };
};

const mapTimeRangeToApiValue = (selectedTimeRange: string): number => {
    if (selectedTimeRange === 'Last 24 hours') return 4;
    if (selectedTimeRange === '7 days') return 5;
    return 4;
};

const renderAlarmTypeCell = (row: Record<string, unknown>) => {
    const alarmText = formatText(row.alarmType);
    const alarmClass = alarmText ? alarmText.toLowerCase() : '';
    return <span className={`alarm-type ${alarmClass}`}>{highlightMatch(alarmText)}</span>;
};

const renderAlertStatusCell = (row: Record<string, unknown>) => {
    const raw = row.status;
    const label = stringifyValue(raw ?? '').toLowerCase();
    if (label === 'active' || raw === 0 || raw === '0') {
        return <span>{highlightMatch('Active')}</span>;
    }
    if (label === 'cleared' || raw === 1 || raw === '1') {
        return <span>{highlightMatch('Cleared')}</span>;
    }
    return <span>{highlightMatch(formatText(raw))}</span>;
};

interface SearchableFilterDropdownProps {
    isOpen: boolean;
    inputValue: string;
    placeholder: string;
    inputClassName?: string;
    onOpen: () => void;
    onSearchChange: (value: string) => void;
    children: React.ReactNode;
}

const SearchableFilterDropdown: React.FC<SearchableFilterDropdownProps> = ({
    isOpen,
    inputValue,
    placeholder,
    inputClassName = 'summary-select searchable-input',
    onOpen,
    onSearchChange,
    children,
}) => (
    <div className="searchable-select-container alerts-filter-field">
        <div className="searchable-select-input">
            <input
                type="text"
                value={inputValue}
                onChange={(e) => onSearchChange(e.target.value)}
                onFocus={onOpen}
                onClick={onOpen}
                onKeyDown={(e) => openDropdownOnArrowKey(e, onOpen)}
                placeholder={placeholder}
                className={inputClassName}
                readOnly={!isOpen}
            />
            <DropdownChevron isOpen={isOpen} />
        </div>
        {isOpen && <div className="searchable-dropdown">{children}</div>}
    </div>
);

const AlertsLoadingView: React.FC = () => (
    <div className="summary-container">
        <div className="summary-filters-container alerts-filters-container">
            <input className="summary-searchbar" placeholder="Search" disabled />
            <select className="summary-select" disabled>
                <option value="">Customer</option>
            </select>
            <select className="summary-select" disabled>
                <option value="">Site</option>
            </select>
            <select className="summary-select" disabled>
                <option value="">Plant</option>
            </select>
            <select className="summary-select" disabled><option>Status</option></select>
        </div>
        <div className="alerts-header">
            <button className="summary-download-btn" disabled>Acknowledge All</button>
        </div>
        <div className="alerts-table-loader">
            <div className="alerts-loader">
                <div className="alerts-loader-spinner"></div>
                <div className="alerts-loader-text">Loading alerts...</div>
            </div>
        </div>
    </div>
);

const AlertsErrorView: React.FC<{ error: string }> = ({ error }) => (
    <div className="alerts-container">
        <div className="alerts-header">
            <div className="alerts-title">Alerts</div>
        </div>
        <div style={{ padding: '30px', textAlign: 'center', color: 'red' }}>{error}</div>
    </div>
);

const useAlertsPage = () => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [customers, setCustomers] = useState<CustomerLite[]>([]);
    const [plants, setPlants] = useState<{ plantId: string; plantName: string }[]>([]);
    const [selectedCustomer, setSelectedCustomer] = useState<string>('');
    const [selectedSite, setSelectedSite] = useState<string>('');
    const [selectedPlant, setSelectedPlant] = useState<string>('');
    const [selectedAlarmType, setSelectedAlarmType] = useState<string>('');
    const [selectedStatus, setSelectedStatus] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
    const [showCalendarModal, setShowCalendarModal] = useState<boolean>(false);
    const [selectedTimeRange, setSelectedTimeRange] = useState<string>('');
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
    const [selectingStart, setSelectingStart] = useState<boolean>(true);
    const calendarModalRef = useRef<HTMLDivElement>(null);
    const [alarmState, setAlarmState] = useState(false);
    const [sortConfig, setSortConfig] = React.useState<{ key: SortableKeys; direction: 'asc' | 'desc' } | null>(null);

    // Searchable dropdown UI state
    const [isCustomerOpen, setIsCustomerOpen] = useState(false);
    const [customerSearch, setCustomerSearch] = useState('');
    const [isSiteOpen, setIsSiteOpen] = useState(false);
    const [siteSearch, setSiteSearch] = useState('');
    const [isPlantOpen, setIsPlantOpen] = useState(false);
    const [plantSearch, setPlantSearch] = useState('');
    const [isAlarmTypeOpen, setIsAlarmTypeOpen] = useState(false);
    const [alarmTypeSearch, setAlarmTypeSearch] = useState('');
    const [isStatusOpen, setIsStatusOpen] = useState(false);
    const [statusSearch, setStatusSearch] = useState('');
    // Add these state variables
   const [extractedSites, setExtractedSites] = useState<ExtractedSite[]>([]);

   const [extractedPlants, setExtractedPlants] = useState<ExtractedPlant[]>([]);
    // Disable outer document scrolling while on Alerts page
    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, []);

    const itemsPerPage = 10;

    // Debounce search term for better UX
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 300);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const filteredCustomersList = React.useMemo(
        () => filterCustomersBySearch(customers || [], customerSearch),
        [customers, customerSearch]
    );

    const filteredSitesList = React.useMemo(
        () =>
            filterSitesBySearch(
                extractedSites,
                selectedCustomer,
                selectedPlant,
                customers,
                siteSearch
            ),
        [extractedSites, selectedCustomer, selectedPlant, customers, siteSearch]
    );

    const filteredPlantsList = React.useMemo(
        () =>
            filterPlantsBySearch(
                extractedPlants,
                selectedSite,
                selectedCustomer,
                plantSearch
            ),
        [extractedPlants, selectedSite, selectedCustomer, plantSearch]
    );
    const alarmTypeOptions = ['Critical', 'Warning','Device Communication Lost'];
    const filteredAlarmTypesList = React.useMemo(() => {
        if (!alarmTypeSearch.trim()) return alarmTypeOptions;
        const term = alarmTypeSearch.toLowerCase();
        return alarmTypeOptions.filter(type => type.toLowerCase().includes(term));
    }, [alarmTypeSearch]);

    const statusOptions = ['Active', 'Cleared'];
    const filteredStatusList = React.useMemo(() => {
        if (!statusSearch.trim()) return statusOptions;
        const term = statusSearch.toLowerCase();
        return statusOptions.filter(status => status.toLowerCase().includes(term));
    }, [statusSearch]);

    // Close any open dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Element | null;
            if (!target) return;
            const inDropdown = target.closest('.searchable-select-container');
            if (!inDropdown) {
                setIsCustomerOpen(false);
                setIsSiteOpen(false);
                setIsPlantOpen(false);
                setIsAlarmTypeOpen(false);
                setIsStatusOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Time range options
    const timeRangeOptions = ['Last 24 hours', '7 days', 'Custom date range'];

    // Calendar handlers
    const handleCalendarToggle = () => {
        setShowCalendarModal(!showCalendarModal);
    };

    const handleTimeRangeSelect = (range: string) => {
        setSelectedTimeRange(range);
        if (range !== 'Custom date range') {
            setShowCalendarModal(false);
        }
    };

    const handleDateRangeApply = () => {
        if (startDate && endDate) {
            setSelectedTimeRange('Custom date range');
            setShowCalendarModal(false);
            const s = new Date(startDate);
            s.setHours(0, 0, 0, 0);
            const e = new Date(endDate);
            e.setHours(23, 59, 59, 999);
            
            const alertParams = {
                startDate: s.toISOString(),
                endDate: e.toISOString(),
                alarmStatus: selectedStatus || 'All',
                timeRange: 6 // Custom date range
            };
            
            fetchAlerts(alertParams);
        }
    };

    const requestSort = (key: SortableKeys) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig?.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortArrow = (columnKey: SortableKeys) => {
        return (
            <SortIcon
                columnKey={columnKey}
                sortConfig={sortConfig}
                onSort={requestSort}
            />
        );
    };

    const renderHeaderContent = (label: string, columnKey: SortableKeys) => (
        <div className="alert-header-cell">
            <span>{label}</span>
            {getSortArrow(columnKey)}
        </div>
    );

    const filteredData = useMemo(() => {
        const term = debouncedSearchTerm?.toLowerCase() ?? '';
        const { startMs, endMs } = getAlertDateRangeMs(selectedTimeRange, startDate, endDate);
        const filterContext: AlertFilterContext = {
            term,
            selectedAlarmType,
            selectedStatus,
            selectedCustomer,
            selectedSite,
            selectedPlant,
            startMs,
            endMs,
            customers,
            extractedSites,
            extractedPlants,
        };

        return data.filter((row) => alertRowMatchesFilters(row, filterContext));
    }, [
        data,
        debouncedSearchTerm,
        selectedAlarmType,
        selectedStatus,
        selectedCustomer,
        selectedSite,
        selectedPlant,
        selectedTimeRange,
        startDate,
        endDate,
        customers,
        extractedSites,
        extractedPlants,
    ]);

    const sortedData = useMemo(() => {
        const sortableItems = [...filteredData];
        if (!sortConfig?.key) {
            return sortableItems;
        }

        sortableItems.sort((a, b) =>
            compareAlertSortValues(
                getAlertSortValue(a, sortConfig.key),
                getAlertSortValue(b, sortConfig.key),
                sortConfig.direction
            )
        );
        return sortableItems;
    }, [filteredData, sortConfig]);
    const navigate = useNavigate();
    const {
        currentPage,
        totalPages,
        currentData,
        totalItems,
        goToPreviousPage,
        goToNextPage,
        goToPage
    } = usePagination({ data: sortedData, itemsPerPage });

    // Reset to first page when the filtered data or any filter changes
    useEffect(() => {
        goToPage(1);
    }, [debouncedSearchTerm, selectedAlarmType, selectedStatus, selectedCustomer, selectedSite, selectedPlant, startDate, endDate]);

    // Reset to first page when sorting changes
    useEffect(() => {
        goToPage(1);
    }, [sortConfig]);
    
 const fetchAlerts = async (alertParams: any) => {
    try {
        setLoading(true);

        const apiParams = {
            alarmStatus: alertParams.alarmStatus || 'All',
            timeRange: alertParams.timeRange || 6,
            startTime: alertParams.startTime,
            endTime: alertParams.endTime,
            startDate: alertParams.startDate,
            endDate: alertParams.endDate,
        };

        const alertsData = await getAllAlerts(apiParams, Intl.DateTimeFormat().resolvedOptions().timeZone);
        const transformedData = transformAlertData(alertsData);
        const { uniqueSites, uniquePlants } = extractSitesAndPlantsFromAlerts(alertsData);

        setData(transformedData);
        setExtractedSites(uniqueSites);
        setExtractedPlants(uniquePlants);
        setPlants(uniquePlants.map((plant) => ({ plantId: plant.plantId, plantName: plant.plantName })));
        setError(null);
    } catch (err) {
        console.error('Failed to fetch alerts:', err);
        setError('Failed to load alerts. Please try again.');
    } finally {
        setLoading(false);
    }
};

    // Fetch customers data on component mount
    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                // Fetch customers using new API endpoint
                const districtCustomerData = await getDistrictCustomerSiteInfo();
                                
                // Ensure we have an array to work with
                if (!Array.isArray(districtCustomerData)) {
                    console.error('Expected array from getDistrictCustomerSiteInfo, received:', districtCustomerData);
                    setCustomers([]);
                    return;
                }
                
                const validCustomers = transformDistrictCustomerData(districtCustomerData);
                
                setCustomers(validCustomers);
            } catch (e) {
                console.error('Failed to fetch customers:', e);
                setCustomers([]);
            }
        };
        fetchCustomers();
        // Initial load with default parameters
        const initialParams = {
            alarmStatus: 'All',
            timeRange: 4 // Default to last 24 hours
        };
        fetchAlerts(initialParams);
    }, []);

    // For acknowledge, need to update the original data array
    const acknowledge = (alertId: string) => {
        const globalIndex = data.findIndex((row) => row.id === alertId);
        if (globalIndex === -1) return;

        const newData = [...data];
        if (newData[globalIndex].canAcknowledge) {
            setLoading(true);
            acknowledgeAllAlerts([alertId])
                .then(() => {
                    newData[globalIndex].acknowledged = true;
                    setData(newData);
                    setError(null);
                    toast.success('Acknowledged successfully');
                })
                .catch((err) => {
                    setError('Failed to acknowledge alert. Please try again.');
                    console.error('Acknowledge error:', err);
                })
                .finally(() => setLoading(false));
        }
    };

    const acknowledgeAll = async () => {
        try {
            setLoading(true);
            const acknowledgeableAlertIds = data
                .filter(row => row.canAcknowledge && !row.acknowledged)
                .map(row => row.id);
            if (acknowledgeableAlertIds.length === 0) {
                setError('No alerts available to acknowledge.');
                setLoading(false);
                return;
            }
            await acknowledgeAllAlerts(acknowledgeableAlertIds);
            const newData = data.map(row =>
                row.canAcknowledge ? { ...row, acknowledged: true } : row
            );
            setData(newData);
            setError(null);
            toast.success('Acknowledged successfully');
        } catch (error) {
            console.error('Failed to acknowledge all alerts:', error);
            setError('Failed to acknowledge alerts. Please try again.');
        } finally {
            setLoading(false);
        }
    };

   const handleAssetNameClick = (asset: any) => {
  const params = new URLSearchParams();
  if (asset.customerId) params.append('customerId', asset.customerId);
  if (asset.siteId) params.append('siteId', asset.siteId);
  if (asset.plantId) params.append('plantId', asset.plantId);
  if (asset.assetId) params.append('selectedAssetId', asset.assetId);

  const queryString = params.toString();
  const url = queryString ? `/asset-details/${asset.assetId}?${queryString}` : `/asset-details/${asset.assetId}`;
  navigate(url, {
    state: {
      assetName: asset.assetName,
      breadcrumbTitle: asset.assetName
    }
  });
};

    // Calendar date functions
    const handleDateClick = (date: Date) => {
        if (selectingStart || !startDate) {
            setStartDate(date);
            setSelectingStart(false);
        } else {
            if (date < startDate) {
                setStartDate(date);
                setEndDate(startDate);
            } else {
                setEndDate(date);
            }
            setSelectingStart(true);
        }
    };

const handleSiteChange = useCallback((siteId: string) => {
  setSelectedSite(siteId);
  setSelectedPlant('');
  setSelectedCustomer(siteId ? resolveCustomerIdForSite(siteId, extractedSites, customers) : '');
}, [extractedSites, customers]);

const handlePlantChange = useCallback((plantId: string) => {
  setSelectedPlant(plantId);

  if (!plantId) {
    setSelectedSite('');
    setSelectedCustomer('');
    return;
  }

  const plantObj = extractedPlants.find((plant) => plant.plantId === plantId);
  if (plantObj?.siteId) {
    setSelectedSite(plantObj.siteId);
  }

  const customerId = resolveCustomerIdForPlant(plantObj, customers);
  if (!customerId && plantObj) {
    console.warn('Could not find customer for plant:', {
      plant: plantObj.plantName,
      customerNameFromPlant: plantObj.customerName,
      customerIdFromPlant: plantObj.customerId,
      availableCustomers: customers.map((customer) => customer.customerName),
    });
  }
  setSelectedCustomer(customerId);
}, [extractedPlants, customers]);

    const getDaysInMonth = (date: Date): Date[] => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const startDateLocal = new Date(firstDay);
        startDateLocal.setDate(startDateLocal.getDate() - firstDay.getDay());
        const days: Date[] = [];
        for (let i = 0; i < 42; i++) {
            days.push(new Date(startDateLocal));
            startDateLocal.setDate(startDateLocal.getDate() + 1);
        }
        return days;
    };

    const navigateMonth = (direction: number) => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1));
    };

    const resetDateSelection = () => {
        setStartDate(null);
        setEndDate(null);
        setSelectingStart(true);
    };

    const isDateInRange = (date: Date): boolean => {
        if (!startDate || !endDate) return false;
        return date >= startDate && date <= endDate;
    };

    const isStartDate = (date: Date): boolean =>
        startDate?.toDateString() === date.toDateString();

    const isEndDate = (date: Date): boolean =>
        endDate?.toDateString() === date.toDateString();

    const getDayBackground = (date: Date): string =>
        getCalendarDayBackground(date, startDate, endDate);

    // Outside click to close calendar
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                showCalendarModal &&
                calendarModalRef.current &&
                !calendarModalRef.current.contains(event.target as Node)
            ) {
                setShowCalendarModal(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showCalendarModal]);

    // Update alarmState and time range based on selectedStatus without refetching
    useEffect(() => {
        if (selectedStatus == "Cleared") {
            setAlarmState(true);
        }
        else {
            setSelectedTimeRange('');
            setAlarmState(false);
        }
    }, [selectedStatus]);

    useEffect(() => {
        if (selectedTimeRange !== '' && selectedTimeRange !== 'Custom date range') {
            fetchAlerts({
                alarmStatus: selectedStatus || 'All',
                timeRange: mapTimeRangeToApiValue(selectedTimeRange),
            });
        }
    }, [selectedTimeRange, selectedStatus]);

    const openFilterDropdown = (target: 'alarmType' | 'customer' | 'site' | 'plant' | 'status') => {
        setIsAlarmTypeOpen(target === 'alarmType');
        setIsCustomerOpen(target === 'customer');
        setIsSiteOpen(target === 'site');
        setIsPlantOpen(target === 'plant');
        setIsStatusOpen(target === 'status');
    };

    const { inputValue: customerInputValue, placeholder: customerInputPlaceholder } =
        resolveCustomerInputDisplay(isCustomerOpen, customerSearch, selectedCustomer, customers);

    const siteInputValue = resolveSiteDisplayValue(
        isSiteOpen,
        siteSearch,
        selectedSite,
        selectedPlant,
        extractedSites,
        extractedPlants
    );

    const selectedPlantName = getSelectedPlantName(selectedPlant, extractedPlants, plants);
    const plantInputValue = isPlantOpen ? plantSearch : selectedPlantName;
    const plantInputPlaceholder = isPlantOpen
        ? 'Search plants...'
        : (selectedPlantName || 'Plant');

    return {
        loading,
        error,
        data,
        searchTerm,
        setSearchTerm,
        isAlarmTypeOpen,
        alarmTypeSearch,
        setAlarmTypeSearch,
        selectedAlarmType,
        setSelectedAlarmType,
        isCustomerOpen,
        customerSearch,
        setCustomerSearch,
        selectedCustomer,
        setSelectedCustomer,
        customerInputValue,
        customerInputPlaceholder,
        isSiteOpen,
        siteSearch,
        setSiteSearch,
        selectedSite,
        siteInputValue,
        isPlantOpen,
        plantSearch,
        setPlantSearch,
        selectedPlant,
        setSelectedPlant,
        plantInputValue,
        plantInputPlaceholder,
        isStatusOpen,
        statusSearch,
        setStatusSearch,
        selectedStatus,
        setSelectedStatus,
        filteredAlarmTypesList,
        filteredCustomersList,
        filteredSitesList,
        filteredPlantsList,
        filteredStatusList,
        openFilterDropdown,
        handleSiteChange,
        handlePlantChange,
        acknowledgeAll,
        alarmState,
        showCalendarModal,
        calendarModalRef,
        handleCalendarToggle,
        selectedTimeRange,
        startDate,
        endDate,
        timeRangeOptions,
        handleTimeRangeSelect,
        getDaysInMonth,
        currentMonth,
        navigateMonth,
        handleDateClick,
        isStartDate,
        isEndDate,
        isDateInRange,
        getDayBackground,
        resetDateSelection,
        handleDateRangeApply,
        requestSort,
        renderHeaderContent,
        currentData,
        handleAssetNameClick,
        acknowledge,
        currentPage,
        totalPages,
        totalItems,
        itemsPerPage,
        goToPreviousPage,
        goToNextPage,
        setIsAlarmTypeOpen,
        setIsCustomerOpen,
        setIsSiteOpen,
        setIsPlantOpen,
        setIsStatusOpen,
        setSelectedSite,
    };
};

type AlertsPageState = ReturnType<typeof useAlertsPage>;
type AlertsPageContentProps = Omit<AlertsPageState, 'loading' | 'error'>;

const AlertsPageContent: React.FC<AlertsPageContentProps> = ({
    data,
    searchTerm,
    setSearchTerm,
    isAlarmTypeOpen,
    alarmTypeSearch,
    setAlarmTypeSearch,
    selectedAlarmType,
    setSelectedAlarmType,
    isCustomerOpen,
    customerSearch,
    setCustomerSearch,
    selectedCustomer,
    setSelectedCustomer,
    customerInputValue,
    customerInputPlaceholder,
    isSiteOpen,
    siteSearch,
    setSiteSearch,
    selectedSite,
    siteInputValue,
    isPlantOpen,
    plantSearch,
    setPlantSearch,
    selectedPlant,
    setSelectedPlant,
    plantInputValue,
    plantInputPlaceholder,
    isStatusOpen,
    statusSearch,
    setStatusSearch,
    selectedStatus,
    setSelectedStatus,
    filteredAlarmTypesList,
    filteredCustomersList,
    filteredSitesList,
    filteredPlantsList,
    filteredStatusList,
    openFilterDropdown,
    handleSiteChange,
    handlePlantChange,
    acknowledgeAll,
    alarmState,
    showCalendarModal,
    calendarModalRef,
    handleCalendarToggle,
    selectedTimeRange,
    startDate,
    endDate,
    timeRangeOptions,
    handleTimeRangeSelect,
    getDaysInMonth,
    currentMonth,
    navigateMonth,
    handleDateClick,
    isStartDate,
    isEndDate,
    isDateInRange,
    getDayBackground,
    resetDateSelection,
    handleDateRangeApply,
    requestSort,
    renderHeaderContent,
    currentData,
    handleAssetNameClick,
    acknowledge,
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    goToPreviousPage,
    goToNextPage,
    setIsAlarmTypeOpen,
    setIsCustomerOpen,
    setIsSiteOpen,
    setIsPlantOpen,
    setIsStatusOpen,
    setSelectedSite,
}) => (
        <div className="summary-container alerts-page">
            <div className="summary-filters-container new-summary-filter alerts-filters-container"  style={{backgroundColor:'#001023' }} >
                <div className="alerts-filters-group alerts-dropdowns-group" style={{marginTop:'10px'}}>
                    {/* Alarm Type searchable dropdown */}
                    <div className="searchable-select-container alerts-filter-field">
                        <div className="searchable-select-input">
                            <input
                                type="text"
                                value={isAlarmTypeOpen ? alarmTypeSearch : (selectedAlarmType || '')}
                                onChange={(e) => setAlarmTypeSearch(e.target.value)}
                                onFocus={() => openFilterDropdown('alarmType')}
                                onClick={() => openFilterDropdown('alarmType')}
                                onKeyDown={(e) => {
                                    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                                        openFilterDropdown('alarmType');
                                    }
                                }}
                                placeholder={isAlarmTypeOpen ? 'Search alarm types...' : (selectedAlarmType || 'Alarm Type')}
                                title={selectedAlarmType || ''}
                                className="summary-select searchable-input alarm-type-filter alert-type-filter"
                                readOnly={!isAlarmTypeOpen}
                            />
                            <DropdownChevron isOpen={isAlarmTypeOpen} />
                        </div>
                        {isAlarmTypeOpen && (
                            <div className="searchable-dropdown">
                                <DropdownOptionButton
                                    isSelected={selectedAlarmType === ''}
                                    onSelect={() => { setSelectedAlarmType(''); setIsAlarmTypeOpen(false); setAlarmTypeSearch(''); }}
                                >
                                    Alarm Type: All
                                </DropdownOptionButton>
                                {filteredAlarmTypesList.map((type, index) => (
                                    <DropdownOptionButton
                                        key={`${type}-${index}`}
                                        isSelected={type === selectedAlarmType}
                                        onSelect={() => { setSelectedAlarmType(type); setIsAlarmTypeOpen(false); setAlarmTypeSearch(''); }}
                                    >
                                        {type}
                                    </DropdownOptionButton>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Customer searchable dropdown */}
<div className="searchable-select-container alerts-filter-field">
  <div className="searchable-select-input">
    <input
      type="text"
      value={customerInputValue}
      onChange={(e) => setCustomerSearch(e.target.value)}
      onFocus={() => openFilterDropdown('customer')}
      onClick={() => openFilterDropdown('customer')}
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          openFilterDropdown('customer');
        }
      }}
      placeholder={customerInputPlaceholder}
      title={customerInputValue}
      className="summary-select searchable-input"
      readOnly={!isCustomerOpen}
    />
    <DropdownChevron isOpen={isCustomerOpen} />
  </div>
  {isCustomerOpen && (
    <div className="searchable-dropdown">
      <DropdownOptionButton
        isSelected={selectedCustomer === ''}
        onSelect={() => { setSelectedCustomer(''); setSelectedSite(''); setSelectedPlant(''); setIsCustomerOpen(false); setCustomerSearch(''); }}
      >
        All Customers
      </DropdownOptionButton>
      {filteredCustomersList.map((customer, index) => (
        <DropdownOptionButton
          key={customer.id || `${customer.customerId}-${index}`}
          isSelected={customer.customerId === selectedCustomer}
          onSelect={() => { setSelectedCustomer(customer.customerId); setSelectedSite(''); setSelectedPlant(''); setIsCustomerOpen(false); setCustomerSearch(''); }}
        >
          {getCustomerLabel(customer)}
        </DropdownOptionButton>
      ))}
    </div>
  )}
</div>

 {/* Site searchable dropdown */}
<div className="searchable-select-container alerts-filter-field">
  <div className="searchable-select-input">
    <input
      type="text"
      value={siteInputValue}
      onChange={(e) => setSiteSearch(e.target.value)}
      onFocus={() => openFilterDropdown('site')}
      onClick={() => openFilterDropdown('site')}
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          openFilterDropdown('site');
        }
      }}
      placeholder={isSiteOpen ? 'Search sites...' : 'Site'}
      title={siteInputValue}
      className="summary-select searchable-input"
      readOnly={!isSiteOpen}
    />
    <DropdownChevron isOpen={isSiteOpen} />
  </div>
  {isSiteOpen && (
    <div className="searchable-dropdown">
      <DropdownOptionButton
        isSelected={selectedSite === ''}
        onSelect={() => {
          setSelectedSite('');
          setSelectedPlant('');
          setIsSiteOpen(false);
          setSiteSearch('');
        }}
      >
        All Sites
      </DropdownOptionButton>
      {filteredSitesList.map((site: any, index: number) => (
        <DropdownOptionButton
          key={`${site.siteId}-${index}`}
          isSelected={site.siteId === selectedSite}
          onSelect={() => {
            handleSiteChange(site.siteId);
            setSelectedPlant('');
            setIsSiteOpen(false);
            setSiteSearch('');
          }}
        >
          {site.site}
        </DropdownOptionButton>
      ))}
    </div>
  )}
</div>

                    {/* Plant searchable dropdown */}
<div className="searchable-select-container alerts-filter-field">
  <div className="searchable-select-input">
    <input
      type="text"
      value={plantInputValue}
      onChange={(e) => setPlantSearch(e.target.value)}
      onFocus={() => openFilterDropdown('plant')}
      onClick={() => openFilterDropdown('plant')}
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          openFilterDropdown('plant');
        }
      }}
      placeholder={plantInputPlaceholder}
      title={plantInputValue}
      className="summary-select searchable-input"
      readOnly={!isPlantOpen}
    />
    <DropdownChevron isOpen={isPlantOpen} />
  </div>
  {isPlantOpen && (
    <div className="searchable-dropdown">
      <DropdownOptionButton
        isSelected={selectedPlant === ''}
        onSelect={() => {
          setSelectedPlant('');
          setIsPlantOpen(false);
          setPlantSearch('');
        }}
      >
        All Plants
      </DropdownOptionButton>
      {filteredPlantsList.map((plant: any, index: number) => (
        <DropdownOptionButton
          key={`${plant.plantId}-${index}`}
          isSelected={plant.plantId === selectedPlant}
          onSelect={() => {
            handlePlantChange(plant.plantId);
            setIsPlantOpen(false);
            setPlantSearch('');
          }}
        >
          {plant.plantName}
        </DropdownOptionButton>
      ))}
    </div>
  )}
</div>

                    {/* Status searchable dropdown */}
                    <div className="searchable-select-container alerts-filter-field">
                        <div className="searchable-select-input">
                            <input
                                type="text"
                                value={isStatusOpen ? statusSearch : (selectedStatus || '')}
                                onChange={(e) => setStatusSearch(e.target.value)}
                                onFocus={() => openFilterDropdown('status')}
                                onClick={() => openFilterDropdown('status')}
                                onKeyDown={(e) => {
                                    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                                        openFilterDropdown('status');
                                    }
                                }}
                                placeholder={isStatusOpen ? 'Search status...' : (selectedStatus || 'Status')}
                                title={selectedStatus || ''}
                                className="summary-select searchable-input status-filter"
                                readOnly={!isStatusOpen}
                            />
                            <DropdownChevron isOpen={isStatusOpen} />
                        </div>
                        {isStatusOpen && (
                            <div className="searchable-dropdown">
                                <DropdownOptionButton
                                    isSelected={selectedStatus === ''}
                                    onSelect={() => { setSelectedStatus(''); setIsStatusOpen(false); setStatusSearch(''); }}
                                >
                                    Status: All
                                </DropdownOptionButton>
                                {filteredStatusList.map((status, index) => (
                                    <DropdownOptionButton
                                        key={`${status}-${index}`}
                                        isSelected={status === selectedStatus}
                                        onSelect={() => { setSelectedStatus(status); setIsStatusOpen(false); setStatusSearch(''); }}
                                    >
                                        {status}
                                    </DropdownOptionButton>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="alerts-filters-group alerts-search-actions-group">
                    {/* Search bar */}
                    <div className="search-container-site alerts-filter-field alerts-search-field">
                        <img className="search-icon-summary-lg" src={SearchIcon} alt="Search" />
                        <input
                            className="summary-searchbar alerts-searchbar"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button
                        className="alerts-acknowledge-btn"
                        onClick={acknowledgeAll}
                        disabled={data.filter(row => row.canAcknowledge && !row.acknowledged).length === 0}
                    >
                        Acknowledge All
                    </button>
                </div>
                {alarmState && (
                    <div className="calendar-button-container" style={{ position: 'relative', zIndex: 7 }}>
                        <button
                            className='alerts-calendar-btn'
                            type="button"
                            id="calendar-btn"
                            aria-expanded={showCalendarModal}
                            aria-haspopup="dialog"
                            aria-label="Select time range"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCalendarToggle();
                            }}
                        >
                            <img src={calendarIcon} alt="" width="16" height="16" />
                            <span style={{ color: '#e0e0e0', fontSize: '14px' }}>
                                {formatCalendarButtonLabel(selectedTimeRange, startDate, endDate)}
                            </span>
                            <img src={ArrowDown} className={showCalendarModal ? 'rotate-180' : ''} alt="" />
                        </button>

                        {showCalendarModal && (
                            <div className='calendar-modal-container' ref={calendarModalRef} style={{
                                position: 'absolute',
                                top: '100%',
                                left: '-222px',
                                background: '#22314a',
                                border: '1px solid #4a5463',
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                                zIndex: 1000,
                                minWidth: '300px'
                                
                            }}>
                                <div style={{ padding: '16px' }}>
                                    <div style={{ marginBottom: '16px' }}>
                                        <h4 style={{ color: '#e0e0e0', margin: '0 0 12px 0', fontSize: '16px' }}>Select Time Range</h4>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {timeRangeOptions.map((option) => (
                                                <button
                                                    key={option}
                                                    className={`time-range-option ${selectedTimeRange === option ? 'active' : ''}`}
                                                    style={{
                                                        background: selectedTimeRange === option ? '#4a5463' : 'transparent',
                                                        color: '#e0e0e0',
                                                        border: '1px solid #4a5463',
                                                        borderRadius: '4px',
                                                        padding: '8px 12px',
                                                        cursor: 'pointer',
                                                        textAlign: 'left'
                                                    }}
                                                    onClick={() => handleTimeRangeSelect(option)}
                                                >
                                                    {option}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {selectedTimeRange === 'Custom date range' && (
                                        <div className="calendar-section">
                                            <div className="calendar-header" style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '12px'
                                            }}>
                                                <button
                                                    className="calendar-nav-btn"
                                                    onClick={() => navigateMonth(-1)}
                                                    style={{
                                                        background: 'transparent',
                                                        border: '1px solid #4a5463',
                                                        color: '#e0e0e0',
                                                        borderRadius: '4px',
                                                        width: '32px',
                                                        height: '32px',
                                                        cursor: 'pointer'
                                                    }}
                                                >‹</button>
                                                <span style={{ color: '#e0e0e0', fontSize: '14px', fontWeight: 'bold' }}>
                                                    {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                                </span>
                                                <button
                                                    className="calendar-nav-btn"
                                                    onClick={() => navigateMonth(1)}
                                                    style={{
                                                        background: 'transparent',
                                                        border: '1px solid #4a5463',
                                                        color: '#e0e0e0',
                                                        borderRadius: '4px',
                                                        width: '32px',
                                                        height: '32px',
                                                        cursor: 'pointer'
                                                    }}
                                                >›</button>
                                            </div>

                                            <div className="calendar-days-header" style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(7, 1fr)',
                                                gap: '2px',
                                                marginBottom: '8px'
                                            }}>
                                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                                                    <div key={day} style={{
                                                        padding: '4px',
                                                        textAlign: 'center',
                                                        fontSize: '12px',
                                                        color: '#bfc9db',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        {day}
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="calendar-grid" style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(7, 1fr)',
                                                gap: '2px'
                                            }}>
                                                {getDaysInMonth(currentMonth).map((date) => (
                                                    <button
                                                        key={date.toDateString()}
                                                        className={`calendar-day ${date.getMonth() === currentMonth.getMonth() ? '' : 'other-month'
                                                            } ${date.toDateString() === new Date().toDateString() ? 'today' : ''
                                                            } ${isStartDate(date) ? 'start-date' : ''
                                                            } ${isEndDate(date) ? 'end-date' : ''
                                                            } ${isDateInRange(date) ? 'in-range' : ''
                                                            }`}
                                                        style={{
                                                            background: getDayBackground(date),
                                                            border: '1px solid rgba(74, 84, 99, 0.3)',
                                                            color: date.getMonth() === currentMonth.getMonth() ? '#e0e0e0' : '#666',
                                                            borderRadius: '4px',
                                                            width: '32px',
                                                            height: '32px',
                                                            fontSize: '12px',
                                                            cursor: date.getMonth() === currentMonth.getMonth() ? 'pointer' : 'default'
                                                        }}
                                                        onClick={() => handleDateClick(date)}
                                                        disabled={date.getMonth() !== currentMonth.getMonth()}
                                                    >
                                                        {date.getDate()}
                                                    </button>
                                                ))}
                                            </div>

                                            {(startDate || endDate) && (
                                                <div className="date-selection-info" style={{
                                                    marginTop: '12px',
                                                    padding: '8px',
                                                    background: 'rgba(74, 84, 99, 0.2)',
                                                    borderRadius: '4px'
                                                }}>
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        fontSize: '12px',
                                                        color: '#bfc9db'
                                                    }}>
                                                        <span>Start: {startDate ? startDate.toLocaleDateString() : 'Not selected'}</span>
                                                        <span>End: {endDate ? endDate.toLocaleDateString() : 'Not selected'}</span>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="calendar-footer" style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                gap: '8px',
                                                marginTop: '16px'
                                            }}>
                                                <button
                                                    className="reset-btn"
                                                    onClick={resetDateSelection}
                                                    style={{
                                                        background: 'transparent',
                                                        border: '1px solid #4a5463',
                                                        color: '#e0e0e0',
                                                        borderRadius: '4px',
                                                        padding: '8px 16px',
                                                        cursor: 'pointer',
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    Reset
                                                </button>
                                                <button
                                                    className={`apply-btn ${(!startDate || !endDate) ? 'disabled' : ''}`}
                                                    onClick={handleDateRangeApply}
                                                    disabled={!startDate || !endDate}
                                                    style={{
                                                        background: (!startDate || !endDate) ? 'rgba(74, 84, 99, 0.5)' : '#4a5463',
                                                        border: '1px solid #4a5463',
                                                        color: (!startDate || !endDate) ? 'rgba(224, 224, 224, 0.5)' : '#e0e0e0',
                                                        borderRadius: '4px',
                                                        padding: '8px 16px',
                                                        cursor: (!startDate || !endDate) ? 'not-allowed' : 'pointer',
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    Apply Custom Range
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            
            <div className="summary-table-wrapper alerts-table-wrapper">
                <div className="alerts-table-scroll clone">
                    <table className="global-table summary-table alert" style={{overflow:'scroll'}}>
                        <thead style={{ position:'sticky',top:'0',zIndex:'5'}}>
                            <tr>
                               
                                <th   onClick={() => requestSort('assetName')} >
                                    {renderHeaderContent('Asset Name', 'assetName')}
                                </th>
                                 <th  onClick={() => requestSort('assetType')}>
                                    {renderHeaderContent('Asset Type', 'assetType')}
                                </th>
                                
                                <th onClick={() => requestSort('alarmType')}>
                                    {renderHeaderContent('Alarm Type', 'alarmType')}
                                </th>
                                <th onClick={() => requestSort('sensors')}>
                                    {renderHeaderContent('Sensors', 'sensors')}
                                </th>
                                <th onClick={() => requestSort('actualMeasurement')}>
                                    {renderHeaderContent('Actual Measurement', 'actualMeasurement')}
                                </th>
                                <th onClick={() => requestSort('thLL')}>
                                    {renderHeaderContent('Low Low', 'thLL')}
                                </th>
                                <th onClick={() => requestSort('thL')}>
                                    {renderHeaderContent('Low', 'thL')}
                                </th>
                                <th onClick={() => requestSort('thH')}>
                                    {renderHeaderContent('High', 'thH')}
                                </th>
                                <th onClick={() => requestSort('thHH')}>
                                    {renderHeaderContent('High High', 'thHH')}
                                </th>
                                <th onClick={() => requestSort('setOn')}>
                                    {renderHeaderContent('Set On', 'setOn')}
                                </th>
                                <th onClick={() => requestSort('clearedOn')}>
                                    {renderHeaderContent('Cleared On', 'clearedOn')}
                                </th>
                                <th onClick={() => requestSort('status')}>
                                    {renderHeaderContent('Status', 'status')}
                                </th>
                                <th onClick={() => requestSort('site')}>
                                    {renderHeaderContent('Site', 'site')}
                                </th>
                                <th onClick={() => requestSort('plant')}>
                                    {renderHeaderContent('Plant', 'plant')}
                                </th>
                                <th onClick={() => requestSort('customer')}>
                                    {renderHeaderContent('Customer', 'customer')}
                                </th >
                                <th onClick={() => requestSort('Actions')}> 
                                    {renderHeaderContent('Actions', 'Actions')}
                                </th>
                            </tr>
                        </thead>
                         <tbody>
                             {currentData.length === 0 ? (
                                 <tr>
                                     <td colSpan={16} className="table-empty">No Data Available</td>
                                 </tr>
                             ) : (
                             currentData.map((row) => (
                             <tr key={row.id}>
                                  <td 
                                   className='asset-link' 
                                   onClick={() => handleAssetNameClick(row)}
                                   style={{ cursor: 'pointer',zIndex:'1',backgroundColor:'#0F1D2E' }}
                                   title={row.assetName || ''}
                                 >
                                 {row.assetName || ''}
                                  </td>
                                    <td >{highlightMatch(formatText(row.assetType))}</td>
                                    
                                    <td>
                                        {renderAlarmTypeCell(row)}
                                    </td>
                                    <td>{highlightMatch(formatText(row.sensors))}</td>
                                    <td>{highlightMatch(formatText(row.actualMeasurement))}</td>
                                    <td>{formatText(row.thLL)}</td>
                                    <td>{formatText(row.thL)}</td>
                                    <td>{formatText(row.thH)}</td>
                                    <td>{formatText(row.thHH)}</td>
                                    <td>{highlightMatch(formatDateTime(row.setOn))}</td>
                                    <td>{highlightMatch(formatDateTime(row.clearedOn))}</td>
                                    <td className='alert-status'>
                                        {renderAlertStatusCell(row)}
                                    </td>
                                    <td>{highlightMatch(formatText(row.site))}</td>
                                    <td>{highlightMatch(formatText(row.plant))}</td>
                                    <td>{highlightMatch(formatText(row.customer))}</td>
                                    <td>
                                      {renderAcknowledgeAction(row, acknowledge)}
                                     </td>
                                </tr>
                            ))
                            )}
                        </tbody>
                    </table>
                </div>
            <div className="alerts-pagination">
                <button className="pagination-btn prev" onClick={goToPreviousPage} disabled={currentPage === 1}><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15.8333 10L4.16668 10M4.16668 10L10 15.8334M4.16668 10L10 4.16669" stroke="#FCFCFD" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
               </svg>Previous</button>
                    <span className="pagination-center-text">
                        {formatPaginationText(totalItems, currentPage, itemsPerPage)}
                    </span>
                <button className="pagination-btn next" onClick={goToNextPage} disabled={currentPage === totalPages}>Next<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4.16675 9.99996H15.8334M15.8334 9.99996L10.0001 4.16663M15.8334 9.99996L10.0001 15.8333" stroke="#FCFCFD" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
             </svg></button>
                </div>
            </div>
           
            <ToastContainer
                className={"toast-container"}
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </div>
);

const Alerts: React.FC = () => {
    const page = useAlertsPage();

    if (page.loading) {
        return <AlertsLoadingView />;
    }

    if (page.error) {
        return <AlertsErrorView error={page.error} />;
    }

    return <AlertsPageContent {...page} />;
};

export default Alerts;